import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WeatherNewsData, WeatherNewsService } from '../../../core/services/weather-news.service';

export interface WnDialogData {
  lat: number;
  lon: number;
  mode: 'today' | 'tomorrow' | 'weekly';
}

@Component({
  selector: 'app-wn-dialog',
  imports: [DatePipe, DecimalPipe, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './wn-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WnDialogComponent implements OnInit {
  readonly data = inject<WnDialogData>(MAT_DIALOG_DATA);
  private readonly weatherNewsService = inject(WeatherNewsService);
  private readonly datePipe = new DatePipe('en-US');

  readonly isLoading = signal<boolean>(true);
  readonly error = signal<boolean>(false);
  readonly weatherData = signal<WeatherNewsData | null>(null);

  readonly title = computed(() => {
    switch (this.data.mode) {
      case 'today': return '今日の天気（1時間ごと）';
      case 'tomorrow': return '明日の天気（1時間ごと）';
      case 'weekly': return '週間天気';
      default: return '天気予報';
    }
  });

  readonly srfData = computed(() => {
    const data = this.weatherData();
    if (!data?.srf) return [];

    const targetDate = new Date();
    if (this.data.mode === 'tomorrow') {
      targetDate.setDate(targetDate.getDate() + 1);
    }
    
    // YYYY-MM-DD でフィルタリング (ISO 8601フォーマットの前提)
    // タイムゾーンがJSTであることを考慮し、YYYY-MM-DDのプレフィックスでマッチ
    const targetDateStr = this.datePipe.transform(targetDate, 'yyyy-MM-dd', '+0900') || '';
    
    return data.srf.filter(item => item.date.startsWith(targetDateStr));
  });

  readonly mrfData = computed(() => {
    return this.weatherData()?.mrf || [];
  });

  async ngOnInit() {
    try {
      this.isLoading.set(true);
      this.error.set(false);
      
      const response = await this.weatherNewsService.getWeather(this.data.lat, this.data.lon);
      if (response && response.wxdata && response.wxdata.length > 0) {
        this.weatherData.set(response.wxdata[0]);
      } else {
        this.error.set(true);
      }
    } catch (e) {
      console.error(e);
      this.error.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  getWeatherText(wxCode: number): string {
    const firstDigit = wxCode.toString()[0];
    if (firstDigit === '1' || wxCode === 500) return '晴れ';
    if (firstDigit === '2' || wxCode === 600) return 'くもり';
    if (firstDigit === '3' || wxCode === 650) return '雨';
    if (firstDigit === '4' || firstDigit === '9') return '雪';
    if (firstDigit === '8') return '大雨/雷';
    return '-';
  }

  getWeatherIcon(wxCode: number): string {
    const firstDigit = wxCode.toString()[0];
    if (firstDigit === '1' || wxCode === 500) return 'wb_sunny';
    if (firstDigit === '2' || wxCode === 600) return 'cloud';
    if (firstDigit === '3' || wxCode === 650) return 'water_drop';
    if (firstDigit === '4' || firstDigit === '9') return 'ac_unit';
    if (firstDigit === '8') return 'thunderstorm';
    return 'help_outline';
  }

  getWindDirection(dirCode: number): string {
    const directions = ['-', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW', 'N'];
    return directions[dirCode] || '-';
  }
}
